import { Dtos } from "@inrev/common";
import type { UseFollowersReturn } from "@portal/api/followers";
import {
	ACTIVE_AGENT_FOLLOWER_REQUIRED,
	isOnlySetupCompleteFollower,
} from "@portal/api/followers/hasActiveAgentFollower";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useMemo, useState } from "react";
import { HiMiniCheck, HiOutlineUsers } from "react-icons/hi2";
import { ConfirmationModalClickWrapper } from "../layout/ConfirmationModalClickWrapper";
import { Modal } from "../layout/Modal";
import { ModalItemWithHeader } from "../layout/ModalItemWithHeader";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

type FollowButtonProps = {
	notFollowingLabel: string;
	isFollowing: boolean | undefined;
	isOnlyFollower: boolean;
	onClick: () => void;
	condensed?: boolean;
	trackingId?: string;
};

export type FollowerControlsProps = UseFollowersReturn & {
	notFollowingLabel: string;
	manageFollowersSubtext: string;
	useFetchAgentPreviews: () => {
		agentPreviews: Dtos.Agent.Preview.Get.Response[] | undefined;
		agentPreviewsLoading: boolean;
		agentPreviewsError: unknown;
	};
	condensed?: boolean;
	hideFollowerCount?: boolean;
	trackingId?: string;
};

export type ManageFollowersModalProps = Omit<FollowerControlsProps, "notFollowingLabel"> & {
	onClose: () => void;
};

const _FollowButton = ({
	notFollowingLabel,
	isFollowing,
	onClick,
	condensed,
	trackingId,
}: Omit<FollowButtonProps, "isOnlyFollower">) => (
	<div
		{...trackingAttrs(trackingId)}
		className={cn(
			"flex items-center justify-center w-fit h-[24px] min-h-[24px] min-w-[50px] leading-[13px] space-x-[5.5px] px-[10px] text-[12.5px] font-[550] text-secondary border hover:text-gray-700 rounded-full cursor-pointer",
			condensed && "h-[22px] min-h-[22px] text-[11.5px] leading-[11px]",
			isFollowing === undefined
				? "animate-pulse"
				: isFollowing
					? "bg-gray-100 border-gray-500"
					: "bg-gray-white border-gray-400 hover:border-gray-500 px-[12px]",
		)}
		onClick={onClick}
	>
		{!!isFollowing && <HiMiniCheck className="text-tertiary stroke-[1] ml-[-1px] mt-[.5px]" />}
		{isFollowing !== undefined ? (isFollowing ? "Following" : notFollowingLabel) : ""}
	</div>
);

export const FollowButton = ({ isOnlyFollower, ...props }: FollowButtonProps) => {
	if (isOnlyFollower)
		return (
			<ConfirmationModalClickWrapper
				shouldConfirm={isOnlyFollower}
				message={ACTIVE_AGENT_FOLLOWER_REQUIRED}
				confirmButtonText="Okay"
				confirmationButtonColor="primary"
				className="items-center font-medium"
				cancelButton={false}
			>
				<_FollowButton {...props} />
			</ConfirmationModalClickWrapper>
		);
	return <_FollowButton {...props} />;
};

export const ManageFollowersModal = ({
	manageFollowersSubtext,
	onClose,
	useFetchAgentPreviews,
	followers,
	createAgentFollower,
	removeAgentFollower,
}: ManageFollowersModalProps) => {
	const { agentPreviews } = useFetchAgentPreviews();
	const isFollowingArr = useMemo(() => {
		if (!!agentPreviews && !!followers) {
			return agentPreviews.map((agent) => followers.some((follower) => follower.id === agent.id));
		}
	}, [agentPreviews, followers]);

	return (
		<Modal onClickOutside={() => onClose()}>
			<ModalItemWithHeader
				onClose={() => onClose()}
				header={
					<div className="flex flex-col space-y-[8px] mb-[4px]">
						<span className="font-[550]">Manage Followers</span>
						<span className="text-[12.5px] text-secondary font-normal max-w-[90%]">
							{manageFollowersSubtext}
						</span>
					</div>
				}
				className="w-[500px] h-fit max-h-[85vh]"
				bodyClassName="overflow-y-auto"
			>
				<div className="flex flex-col w-full divide-y-[1px] text-[14.5px] divide-gray-100/80">
					{(() => {
						if (!followers || !isFollowingArr || !agentPreviews) {
							return (
								<div className="flex items-center px-[8px] text-[12px] text-secondary italic h-[32px] m-[12px] bg-gray-100 animate-pulse rounded-md">
									Loading followers..
								</div>
							);
						}
						return agentPreviews.map((agent, index) => (
							<div
								key={agent.id}
								className="flex items-center justify-between space-x-[5px] font-normal text-gray-700 px-[24px] py-[18px]"
							>
								<span>{agent.displayName}</span>
								<FollowButton
									notFollowingLabel="Set as follower"
									isFollowing={isFollowingArr[index]}
									isOnlyFollower={
										!!isFollowingArr[index] && isOnlySetupCompleteFollower(followers, agent.id)
									}
									onClick={() => {
										if (isFollowingArr[index]) {
											removeAgentFollower(agent);
										} else {
											createAgentFollower(agent);
										}
									}}
								/>
							</div>
						));
					})()}
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};

export const FollowerControls = (props: FollowerControlsProps) => {
	const [manageFollowersModalOpen, setManageFollowersModalOpen] = useState<boolean>(false);
	const { user } = useAuthenticatedUser();

	if (props.isFollowing === undefined || !props.followers)
		return <div className="w-[90px] h-[24px] rounded-full bg-gray-100 animate-pulse"></div>;
	return (
		<div data-testid="follower-controls" className="flex items-center space-x-[6px]">
			<FollowButton
				notFollowingLabel={props.notFollowingLabel}
				isFollowing={props.isFollowing}
				isOnlyFollower={
					!!props.isFollowing && isOnlySetupCompleteFollower(props.followers, user.id)
				}
				onClick={() => {
					if (props.isFollowing === undefined) return;
					props.isFollowing
						? props.removeAgentFollower(undefined)
						: props.createAgentFollower(undefined);
				}}
				condensed={props.condensed}
				trackingId={props.trackingId}
			/>
			{!props.hideFollowerCount && !!props.followers && (
				<div
					{...trackingAttrs(props.trackingId ? `${props.trackingId}-manage` : undefined)}
					className={cn(
						"flex items-center justify-center w-fit h-[24px] min-h-[24px] space-x-[5.5px] px-[10px] text-[12.5px] font-[550] text-gray-700 border border-gray-400 bg-white hover:text-gray-800 hover:border-gray-600 rounded-full cursor-pointer",
						props.condensed && "h-[22px] min-h-[22px] text-[11.5px]",
					)}
					onClick={() => setManageFollowersModalOpen(true)}
				>
					<span>{props.followers.length}</span>
					<HiOutlineUsers className="text-[13px] text-secondary stroke-[1.75] mt-[1px]" />
				</div>
			)}
			{manageFollowersModalOpen && (
				<ManageFollowersModal {...props} onClose={() => setManageFollowersModalOpen(false)} />
			)}
		</div>
	);
};
